import { useEffect } from 'react'
import { useMap } from 'react-leaflet'
import { CoordinateData } from '../../utils/coordinates'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { useEnv } from '../../utils/useEnv'
import { MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM, MAP_MAX_ZOOM, MAP_MIN_ZOOM } from './mapConfig'

interface MapControllerProps {
  coordinates: CoordinateData[]
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

/**
 * Headless component that owns imperative map setup: zoom limits and
 * persisting / restoring the viewport across openings of the filter.
 */
const MapController = ({ coordinates }: MapControllerProps) => {
  const map = useMap()
  const { geographicMapViewport } = useContextMotifDetails()
  const { isKiosk } = useEnv()

  useEffect(() => {
    map.setMinZoom(MAP_MIN_ZOOM)
    map.setMaxZoom(MAP_MAX_ZOOM)

    const saveViewport = () => {
      const center = map.getCenter()
      geographicMapViewport.current = {
        center: [center.lat, center.lng],
        zoom: map.getZoom(),
      }
    }

    map.on('moveend zoomend', saveViewport)
    saveViewport()
    return () => {
      map.off('moveend zoomend', saveViewport)
    }
  }, [geographicMapViewport, map])

  useEffect(() => {
    // the default prefix is a link to leafletjs.com, which would let kiosk visitors leave the app
    if (isKiosk) map.attributionControl?.setPrefix(false)
  }, [isKiosk, map])

  useEffect(() => {
    if (coordinates.length === 0) return

    const savedViewport = geographicMapViewport.current
    if (savedViewport) {
      map.setView(savedViewport.center, clamp(savedViewport.zoom, MAP_MIN_ZOOM, MAP_MAX_ZOOM))
      return
    }
    map.setView(MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM)
  }, [coordinates, geographicMapViewport, map])

  return null
}

export default MapController
